import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, ListChecks, RotateCw, SearchX } from "lucide-react";
import type { Lead } from "@ir/types";
import {
  formatDateTime,
  fullName,
  searchLeads,
  serializeDaveLeadPayload,
} from "@ir/services";
import {
  Badge,
  Button,
  CopyButton,
  CopyableId,
  DataTable,
  IconButton,
  PageHeader,
  Pagination,
  SearchInput,
  Toolbar,
  type Column,
  type SortState,
} from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";

const PAGE_SIZE = 25;
type LeadSortBy = "created_at" | "first_name" | "surname" | "email" | "mobile" | "type";

function leadSortByForColumn(columnId: string): LeadSortBy {
  if (columnId === "name") return "first_name";
  if (["surname", "email", "mobile", "type", "created_at"].includes(columnId)) {
    return columnId as LeadSortBy;
  }
  return "created_at";
}

export function LeadsPage() {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshToken, setRefreshToken] = useState(0);

  const searchTerm = searchParams.get("q") ?? "";
  const page = Number(searchParams.get("page") ?? "1") || 1;
  const sortBy = searchParams.get("sortBy") ?? "created_at";
  const sortDirection = searchParams.get("sortDirection") === "asc" ? "asc" : "desc";

  const updateParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          }
          if (!("page" in changes)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const result = useAsyncData(
    (signal) =>
      searchLeads({
        searchTerm,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: leadSortByForColumn(sortBy),
        sortDirection,
        signal,
      }),
    [searchTerm, page, sortBy, sortDirection, refreshToken],
  );

  const columns = useMemo<Column<Lead>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        width: "20rem",
        sortable: true,
        cell: (lead) => (
          <div className="min-w-0 max-w-80">
            <p className="truncate font-medium text-content">
              {fullName([lead.first_name, lead.surname])}
            </p>
            <div className="flex min-w-0 items-center gap-1">
              <p className="min-w-0 truncate text-xs text-content-subtle" title={lead.email ?? undefined}>
                {lead.email ?? "No email"}
              </p>
              {lead.email && <CopyButton value={lead.email} label="Copy email" className="size-6" />}
            </div>
          </div>
        ),
      },
      {
        id: "mobile",
        header: "Mobile",
        width: "9rem",
        hideBelow: "md",
        sortable: true,
        cell: (lead) => (
          <span className="whitespace-nowrap tabular text-content-muted">{lead.mobile ?? "—"}</span>
        ),
      },
      {
        id: "customer_reference",
        header: "Ref",
        mobileLabel: "Customer ref",
        width: "8rem",
        hideBelow: "lg",
        cell: (lead) =>
          lead.reference ? (
            <CopyableId value={lead.reference} />
          ) : (
            <span className="text-content-subtle">—</span>
          ),
      },
      {
        id: "other_phone",
        header: "Other phone",
        width: "9rem",
        hideBelow: "xl",
        cell: (lead) => (
          <span className="whitespace-nowrap tabular text-content-muted">
            {lead.other_phone ?? "—"}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        width: "8rem",
        hideBelow: "lg",
        sortable: true,
        cell: (lead) =>
          lead.type ? <Badge tone="gold">{lead.type}</Badge> : <span>—</span>,
      },
      {
        id: "status",
        header: "Status",
        width: "8rem",
        hideBelow: "lg",
        cell: (lead) =>
          lead.partial ? (
            <Badge tone="warning" dot>
              Partial
            </Badge>
          ) : (
            <Badge tone="success" dot>
              Complete
            </Badge>
          ),
      },
      {
        id: "created_at",
        header: "Created",
        width: "11rem",
        hideBelow: "lg",
        sortable: true,
        cell: (lead) => (
          <span className="whitespace-nowrap text-content-muted">
            {formatDateTime(lead.created_at ?? lead.timestamp ?? lead._ts)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        mobileLabel: "Actions",
        align: "right",
        width: "7rem",
        cell: (lead) => (
          <div className="flex items-center justify-end gap-1">
            {can("devtools.use") && (
              <CopyButton value={serializeDaveLeadPayload(lead)} label="Copy Marketplace JSON" />
            )}
            <Link to={`/leads/${lead.id}`} onClick={(event) => event.stopPropagation()}>
              <IconButton icon={Eye} label="View lead details" size="sm" />
            </Link>
          </div>
        ),
      },
    ],
    [can],
  );

  const columnVisibility = useColumnVisibility(
    "leads",
    columns,
    ["name", "mobile", "customer_reference", "type", "status", "created_at"],
  );

  const sort: SortState = {
    columnId: sortBy === "created_at" ? "created_at" : sortBy === "first_name" ? "name" : sortBy,
    direction: sortDirection,
  };

  const updateSort = (next: SortState) => {
    const nextSortBy = leadSortByForColumn(next.columnId);
    updateParams({ sortBy: nextSortBy, sortDirection: next.direction });
  };

  return (
    <>
      <PageHeader
        icon={ListChecks}
        title="Leads"
        description="Raw submissions from the quote form, before they are mapped into a quote."
        actions={
          <Button
            variant="secondary"
            icon={RotateCw}
            loading={result.refreshing}
            onClick={() => setRefreshToken((token) => token + 1)}
          >
            Refresh
          </Button>
        }
      >
        <Toolbar>
          <SearchInput
            className="flex-1"
            value={searchTerm}
            onChange={(value) => updateParams({ q: value })}
            placeholder="Search by reference, name, phone, email or postcode"
            ariaLabel="Search leads"
          />
          {searchTerm && (
            <Button variant="ghost" onClick={() => setSearchParams({}, { replace: true })}>
              Clear
            </Button>
          )}
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
        </Toolbar>
      </PageHeader>

      <DataTable
        caption="Leads"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(lead) => lead.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={SearchX}
        emptyTitle={searchTerm ? "No leads match that search" : "No leads yet"}
        emptyDescription={
          searchTerm
            ? "Try a shorter search term — partial names and postcodes both work."
            : "Leads appear here the moment someone submits the quote form."
        }
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={result.data.pageSize}
          disabled={result.refreshing}
          onPageChange={(next) => updateParams({ page: String(next) })}
        />
      )}
    </>
  );
}
